import Button from '@monofront/design-system/components/Button';
import { RichText } from 'prismic-reactjs';
import PrismicLink from '@components/PrismicLink';
import { IHeroBanner, HERO_BANNER_TYPE } from './types';
import CustomPrismicLink from '@components/CustomPrismicLink';
import { useGiftItem } from '@hooks/businessContainer';
import CompleteRegistration from '@components/CompleteRegistration';
import EmailField from './EmailField';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapHeroBanner } from './mapHeroBanner';
import { loader } from '@monofront/design-system/utils/images';
import { useAuthentication } from '@context/businessContainer';

const HeroBanner: React.FC<IHeroBanner> = ({
  bgDesktopImg,
  bgMobileImg,
  ctaDesc,
  ctaLabel,
  ctaLabelFinishSubscription,
  ctaLink,
  desc,
  title,
  type,
}) => {
  const { isAuthenticated, isMemberProspect } = useAuthentication();
  const { addGiftItemToShoppingCart } = useGiftItem();

  return (
    <>
      <div className="container">
        <div className="bg">
          <picture>
            <source
              media="(max-width: 800px)"
              srcSet={`${loader({
                src: bgMobileImg.url,
                width: 1800,
              })}`}
              sizes="100vw"
            />
            <source
              srcSet={`${loader({
                src: bgDesktopImg.url,
                width: 3840,
              })}`}
              sizes="3840px"
            />
            <img
              src={loader({ src: bgMobileImg.url, width: 1920 })}
              // eslint-disable-next-line react/no-unknown-property
              fetchPriority="high"
              alt="background image"
              className="tw-h-[200px] tw-w-full tw-overflow-hidden tw-object-cover desktop:tw-h-[360px]"
            />
          </picture>
        </div>
        <div className="overlay-wrapper">
          <div className="overlay limited-width-container">
            <div className="card tw-bg-white">
              <div className="card-content">
                <div>
                  <RichText render={title} />
                </div>
                <div className="desc">
                  <RichText render={desc} />
                </div>
                <div className="cta">
                  {type === HERO_BANNER_TYPE.WITH_EMAIL_FIELD ? (
                    ctaLabel && <EmailField ctaLabel={ctaLabel} />
                  ) : isAuthenticated && isMemberProspect ? (
                    <CompleteRegistration label={ctaLabelFinishSubscription} />
                  ) : (
                    ctaLabel && (
                      <PrismicLink link={ctaLink}>
                        <Button
                          block={false}
                          hasMinWidth={false}
                          onClick={addGiftItemToShoppingCart}
                        >
                          {ctaLabel}
                        </Button>
                      </PrismicLink>
                    )
                  )}
                  <div className="cta-text">
                    <RichText
                      render={ctaDesc}
                      serializeHyperlink={CustomPrismicLink}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .container {
          display: flex;
          position: relative;
          min-height: 360px;
          margin-top: var(--negative-space-8);
        }

        .card-content {
          max-width: 440px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: var(--space-6) var(--space-7);
        }

        .card-content :global(h1) {
          font-size: var(--font-size-4);
        }

        .card-content :global(p) {
          font-size: var(--font-size-2);
          font-weight: var(--font-weight-light);
        }

        .cta {
          display: flex;
          align-items: center;
          flex-direction: column;
        }

        .cta :global(p) {
          font-size: var(--font-size-1);
          font-weight: var(--font-weight-light);
        }

        .overlay-wrapper {
          position: absolute;
          right: 0;
          left: 0;
        }
        .overlay {
          display: flex;
          align-items: center;
          margin-top: 50px;
          max-width: var(--header-layout-max-width);
        }

        .card {
          width: auto;
          border-radius: var(--rd-3);
          overflow: hidden;
        }

        .desc {
          margin-top: var(--space-2);
          margin-bottom: var(--space-4);
        }

        .desc > :global(*) {
          font-family: var(--font-family-2);
        }

        .cta-text {
          margin-top: var(--space-1);
        }

        .bg > :global(span) {
          min-height: 360px;
        }

        @media (max-width: 1488px) {
          .overlay {
            max-width: var(--layout-max-width);
          }
        }

        @media (max-width: 800px) {
          .container :global(h1) {
            font-size: var(--font-size-4);
          }

          .card-content {
            max-width: 100%;
            text-align: center;
          }

          .container {
            display: block;
            min-height: 0;
          }

          .cta {
            width: 100%;
          }

          .bg > :global(span) {
            min-height: 200px;
          }

          .overlay-wrapper {
            position: relative;
            right: 0;
            left: 0;
          }

          .overlay {
            margin-top: var(--negative-space-5);
            margin-bottom: var(--negative-space-4);
            padding: 0 var(--space-4);
            width: 100%;
          }

          .card-content {
            padding: var(--space-5);
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default withSlice(HeroBanner, mapHeroBanner);
